import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Header } from './Header';
import { Sidebar } from '@/app/admin/components/layout/Sidebar';
import {
  ReactNode,
  useMemo,
  createContext,
  useContext,
  useState,
  useCallback,
} from 'react';

interface LayoutProps {
  children: ReactNode;
}

interface RouteConfig {
  title: string;
  subtitle: string;
  showBackButton: boolean;
  backButtonText?: string;
  backPath?: string;
}

// Context for dynamic header updates
interface LayoutContextType {
  updateHeader: (config: Partial<RouteConfig>) => void;
}

const LayoutContext = createContext<LayoutContextType | null>(null);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a Layout component');
  }
  return context;
};

export const Layout = ({ children }: LayoutProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  // Base route configuration
  const baseRouteConfig: RouteConfig = useMemo(() => {
    const path = location.pathname;

    switch (path) {
      case '/dashboard':
        return {
          title: 'Market Dashboard',
          subtitle: 'Pricing and insights',
          showBackButton: false,
        };

      case '/admin':
        return {
          title: 'User Management',
          subtitle: 'Manage users and their permissions',
          showBackButton: false,
        };

      case '/admin/dashboard':
        return {
          title: 'Admin Dashboard',
          subtitle: 'Analytics and insights',
          showBackButton: false,
        };

      case '/admin/flagged-products':
        return {
          title: 'Flagged Products',
          subtitle: 'Review and manage flagged products',
          showBackButton: false,
        };

      case '/admin/create-user':
        return {
          title: 'Create New User',
          subtitle: 'Step 1 of 3',
          showBackButton: true,
          backButtonText: 'Back to Users',
          backPath: '/admin',
        };

      default:
        // Handle dynamic routes like /product/:id
        if (path.startsWith('/product/')) {
          const productId = path.split('/product/')[1];
          return {
            title: `${productId} - Market Intelligence`,
            subtitle: 'Pricing and insights',
            showBackButton: true,
            backButtonText: 'Back to Dashboard',
            backPath: '/dashboard',
          };
        }

        // Handle dynamic routes like /admin/user/:userId
        if (path.startsWith('/admin/user/')) {
          const userId = path.split('/admin/user/')[1];
          return {
            title: 'Nabt-Intel',
            subtitle: `User Preview - User ${userId}`,
            showBackButton: true,
            backButtonText: 'Back to Admin',
            backPath: '/admin',
          };
        }

        // Default fallback
        return {
          title: 'Nabt-Intel',
          subtitle: 'Dashboard',
          showBackButton: false,
        };
    }
  }, [location.pathname]);

  // State for dynamic header updates
  const [dynamicConfig, setDynamicConfig] = useState<Partial<RouteConfig>>({});

  // Merge base config with dynamic config
  const routeConfig: RouteConfig = useMemo(
    () => ({
      ...baseRouteConfig,
      ...dynamicConfig,
    }),
    [baseRouteConfig, dynamicConfig]
  );

  const updateHeader = useCallback((config: Partial<RouteConfig>) => {
    setDynamicConfig(config);
  }, []);

  const handleBackClick = useCallback(() => {
    if (routeConfig.backPath) {
      navigate(routeConfig.backPath);
    }
  }, [routeConfig.backPath, navigate]);

  const isAdmin = user?.role === 'admin';

  return (
    <LayoutContext.Provider value={{ updateHeader }}>
      <div className="min-h-screen bg-slate-50">
        {isAdmin && <Sidebar onLogout={handleLogout} />}
        <div
          className={
            isAdmin
              ? 'flex flex-col min-h-screen lg:ml-64'
              : 'flex flex-col min-h-screen'
          }
        >
          <Header
            title={routeConfig.title}
            subtitle={routeConfig.subtitle}
            showBackButton={routeConfig.showBackButton}
            onBackClick={handleBackClick}
            backButtonText={routeConfig.backButtonText}
            showLogo={!isAdmin}
            userName={!isAdmin ? user?.name : undefined}
            onLogout={!isAdmin ? handleLogout : undefined}
          />
          <main className="overflow-auto flex-1">{children}</main>
        </div>
      </div>
    </LayoutContext.Provider>
  );
};
